import { useFormik } from 'formik';
import { useSelector } from 'react-redux';
import {
  useAlert,
  Messages,
  useNavigate,
  ChildrenProps,
  ActionQueueType,
} from '@gv/triage-components';
import {
  useRef,
  useMemo,
  useState,
  useEffect,
  useContext,
  useCallback,
  createContext,
} from 'react';

import { api } from 'api';
import { CommContext } from 'context/comm';
import { endChat } from 'api/subscription';
import { pickUpStreamRoom } from 'utils/config';
import { getUser, getTokens } from 'utils/storage';
import { selectAuthUser } from 'store/slices/auth';
import { getDynamicFormInitialValues } from 'utils/initials';
import { useBitwerxUpdateMutation } from 'store/api/clients';
import { useAblyChatChannel } from 'hooks/useAblyChatChannel';
import { RootState, useAppDispatch, useAppSelector } from 'store';
import { selectClients, useUpdateMutation } from 'store/api/clients';
import { setOnVideoCall, setVideoCallEnded } from 'store/slices/call';
import { initialValues, validationSchema } from 'components/client-list';
import {
  responseCommQueue,
  setCurrentTaskClient,
} from 'store/api/action-queue/thunks';
import {
  removeTask,
  updateTask,
  removeTaskForResponse,
} from 'store/api/action-queue';
import {
  isMineTask,
  getClientPhonesPayload,
  getClientPhonesInitialValues,
} from 'utils/helpers';
import {
  selectCurrentTask,
  selectCurrentTaskForm,
  selectTaskForResponse,
} from 'store/api/action-queue/selectors';

import { useForm } from '../form';
import { useCommEntities } from '../hooks';
import {
  HangUpDialog,
  ReconnectDialog,
  SubmitCaseDialog,
  ConnectionLostDialog,
} from '../dialogs';

import { useConnectionState } from './connection-states';
import { CallContextProps, SelectClientFormValues } from './types';

export const CallContext = createContext<CallContextProps>(
  {} as CallContextProps
);

export const CallContextProvider = ({
  children,
}: ChildrenProps): JSX.Element => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { handleError, showErrorAlert } = useAlert();
  const currentTask = useAppSelector(selectCurrentTask);
  const taskForResponse = useAppSelector(selectTaskForResponse);
  const user = useAppSelector(selectAuthUser);
  const clients = useAppSelector(selectClients);
  const commData = useContext(CommContext);
  const caseForm = useAppSelector(selectCurrentTaskForm);
  const isDynamicFlow = !!currentTask?.h_is_dynamic_flow_enabled;

  // Aggressively disable all beforeunload warnings
  useEffect(() => {
    // Method 1: Remove the default handler
    window.onbeforeunload = null;

    // Method 2: Override with empty function
    window.onbeforeunload = () => {};

    // Method 3: Capture phase event listener to prevent bubbling
    const preventUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.stopImmediatePropagation();
      e.stopPropagation();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', preventUnload, { capture: true });

    // Method 4: Override the preventDefault method
    const originalPreventDefault = Event.prototype.preventDefault;
    Event.prototype.preventDefault = function () {
      if (this.type === 'beforeunload') {
        return;
      }
      originalPreventDefault.call(this);
    };

    // Method 5: Remove any existing listeners
    const removeExistingListeners = () => {
      const eventListeners =
        (window as any).getEventListeners?.(window)?.beforeunload || [];
      eventListeners.forEach((listener: any) => {
        window.removeEventListener('beforeunload', listener.listener);
      });
    };
    removeExistingListeners();

    return () => {
      // Cleanup
      window.onbeforeunload = null;
      window.removeEventListener('beforeunload', preventUnload, {
        capture: true,
      });
      Event.prototype.preventDefault = originalPreventDefault;
      removeExistingListeners();
    };
  }, []);

  const { client: clientData } = useCommEntities();

  const ablyChatChannel = useAblyChatChannel(currentTask?.channelName);

  const initialFormikValues = useMemo(
    () =>
      isDynamicFlow
        ? getDynamicFormInitialValues(caseForm, clientData)
        : caseForm,

    [isDynamicFlow, clientData, caseForm]
  );

  const { activeVideoCall } = useSelector((state: RootState) => state.call);
  const { token } = getTokens();

  const removeCurrentTask = async () => {
    if (currentTask) {
      await dispatch(removeTask({ user, task: currentTask }));
    }
  };

  const [updateClient] = useUpdateMutation();
  const [updateBitwerxClient] = useBitwerxUpdateMutation();
  const isCompletely = useRef<boolean>(false);
  const autoResponseInProgress = useRef<boolean>(false);
  const { activeCall, setActiveCall, setCallbackOpened } = commData;

  const [isHangUpDialogVisible, setHangUpDialogVisible] = useState(false);
  const [isSubmitDialogVisible, setSubmitDialogVisible] = useState(false);
  const [isCallTransferred, setCallTransferred] = useState(false);

  const {
    isLostDialogVisible,
    setLostDialogVisible,
    isReconnectAvailable,
    setReconnectialogVisible,
    isReconnectDialogVisible,
  } = useConnectionState(currentTask);

  useEffect(() => {
    if (!taskForResponse || autoResponseInProgress.current) {
      return;
    }
    const response = async () => {
      try {
        autoResponseInProgress.current = true;
        await dispatch(
          responseCommQueue({
            navigate,
            isLocal: true,
            isContinue: true,
            task: taskForResponse,
            ...commData,
          })
        ).unwrap();
      } catch (error) {
        handleError(error);
        dispatch(removeTaskForResponse());
      } finally {
        autoResponseInProgress.current = false;
      }
    };
    response();
  }, [dispatch, taskForResponse, commData, navigate, handleError]);

  const onReconnect = useCallback(async () => {
    if (!isReconnectAvailable || !currentTask) {
      return;
    }
    try {
      await dispatch(
        responseCommQueue({
          navigate,
          task: currentTask,
          ...commData,
        })
      ).unwrap();
    } catch (error) {
      handleError(error);
    }
  }, [
    dispatch,
    commData,
    navigate,
    handleError,
    currentTask,
    isReconnectAvailable,
  ]);

  const endTask: CallContextProps['endTask'] = useCallback(
    async (data) => {
      try {
        console.log('endTask', JSON.stringify(data), !!currentTask, !!user);
        const { end, caseId, completely } = data || {};
        const sleep = (ms: number) =>
          new Promise((resolve) => setTimeout(resolve, ms));
        if (end && currentTask?.CSID) {
          const isMine = isMineTask(currentTask, getUser()?.id);
          if (isMine) {
            console.log('end conference');
            await api.comm.endConference({ CSID: currentTask.CSID });
          } else {
            console.log(
              "don't end conference because this call is not mine",
              currentTask.assignTo
            );
          }
        }
        if (
          currentTask?.channelName &&
          currentTask.type === ActionQueueType.Chat &&
          completely &&
          caseId
        ) {
          await endChat(currentTask.channelName, {
            status: 3,
            case_id: caseId ?? 0,
            end_by: user?.id ?? 0,
            hospital_id: currentTask.h_id ?? 0,
          });
          await api.comm.userChatStatusUpdate({
            userId: user?.id ?? 0,
            ParticipantLabel: 'Agent',
            callStatus: 'participant-leave',
            callSid: currentTask.channelName,
          });
        }
        if (end && currentTask?.channelName) {
          if (currentTask.type === ActionQueueType.Video) {
            await api.comm.userCallStatusUpdate({
              userId: user?.id ?? 0,
              ParticipantLabel: 'Agent',
              callStatus: 'participant-leave',
              callSid: currentTask.channelName,
            });
          } else if (currentTask.type === ActionQueueType.Chat) {
            await api.comm.userChatStatusUpdate({
              userId: user?.id ?? 0,
              ParticipantLabel: 'Agent',
              callStatus: 'participant-leave',
              callSid: currentTask.channelName,
            });
          }
          await api.comm.endChat(currentTask.channelName);
        }
        console.log(activeCall, 'activeCall==============');
        if (activeCall) {
          console.log(
            'call disconnected by end task',
            activeCall.parameters.CallSid
          );
          activeCall.disconnect();
          await api.comm.userCallStatusUpdate({
            userId: user?.id ?? 0,
            ParticipantLabel: 'Agent',
            callStatus: 'participant-leave',
            callSid: activeCall.parameters.CallSid,
          });
          setActiveCall(undefined);
        }

        const callEndedByUser = end && !completely;

        if (currentTask) {
          if (completely && user) {
            await removeCurrentTask();
          } else if (callEndedByUser) {
            await dispatch(
              updateTask({
                changes: { ...currentTask, $callEndedByUser: true },
              })
            );
            if (currentTask.type === ActionQueueType.Video) {
              await activeVideoCall?.endCall();
              pickUpStreamRoom({ startVideo: false, userId: currentTask.u_id });
              await removeCurrentTask();
              navigate('/action-center/queue');
              dispatch(setOnVideoCall(true));
              await sleep(30000);
              const getCall = await activeVideoCall?.get();
              const recordingData = await activeVideoCall?.queryRecordings(
                getCall?.call?.session?.id
              );
              await api.videoCall.endVideoCall(
                user?.hospital_id,
                {
                  status: 3,
                  case_id: caseId,
                  end_by: user?.id,
                  room_id: currentTask.channelName,
                  file: recordingData?.recordings[0]?.url,
                },
                token,
                dispatch
              );
            }
            if (
              currentTask?.channelName &&
              currentTask?.type === ActionQueueType.Chat &&
              caseId
            ) {
              await endChat(currentTask.channelName, {
                status: 3,
                case_id: caseId ?? 0,
                end_by: user?.id ?? 0,
                hospital_id: currentTask.h_id ?? 0,
              });
              ablyChatChannel?.publish('end-chat', {
                status: '3',
                type: 'end-chat',
                case_id: caseId ?? 0,
                end_by: user?.id ?? 0,
                name: user?.name ?? '',
                hospital_id: currentTask.h_id ?? 0,
                room_id: currentTask?.channelName ?? '',
              });
              navigate('/action-center/queue');
              dispatch(setOnVideoCall(true));
            }
          }
        }
        dispatch(setVideoCallEnded(false));
      } catch (error) {
        handleError(error);
      }
    },
    [currentTask, user, clients, dispatch, navigate, handleError]
  );

  const onSelectClient = async (values: SelectClientFormValues) => {
    const { selectedClientId } = values;

    if (!selectedClientId) {
      showErrorAlert(Messages.CLIENT_ID_NOT_FOUND);
      return;
    }

    const client = clients?.[selectedClientId];

    if (!client) {
      showErrorAlert(Messages.CLIENT_NOT_FOUND_TO_SELECT);
      return;
    }

    try {
      let taskClient = client;
      if (!values?.isPimsRecord) {
        if (currentTask?.from) {
          const phonesPayload = getClientPhonesPayload(
            getClientPhonesInitialValues({
              ...taskClient,
              phones: [...(taskClient?.phones ?? []), currentTask.from],
            })
          );
          const updatedClient: any = await updateClient({
            ...phonesPayload,
            client_id: selectedClientId,
          }).unwrap();
          if (
            updatedClient?.message_alert &&
            updatedClient?.message_alert !== ''
          ) {
            showErrorAlert(updatedClient?.message_alert);
          }
          taskClient = updatedClient;
        } else {
          console.error(
            "can't update client because task w/o from",
            currentTask ? JSON.stringify(currentTask) : null
          );
        }
        await dispatch(
          setCurrentTaskClient({
            client: taskClient,
          })
        ).unwrap();
      } else {
        try {
          if (currentTask?.from) {
            const phonesPayload = [currentTask.from];

            const response = await updateBitwerxClient({
              phones: phonesPayload,
              hospitalId: currentTask!.h_id!,
              bitwerx_client_id: selectedClientId,
            }).unwrap();
            await dispatch(
              setCurrentTaskClient({
                client: response,
              })
            ).unwrap();
          } else {
            console.error(
              "can't update client because task w/o from",
              currentTask ? JSON.stringify(currentTask) : null
            );
          }
        } catch (e) {
          handleError(e);
        }
        navigate(-1);
      }
    } catch (e) {
      handleError(e);
    }
  };

  const form = useForm({
    endTask,
    isCompletely,
    isCallTransferred,
    setCallTransferred,
  });

  const selectClientForm = useFormik<SelectClientFormValues>({
    initialValues,
    validationSchema,
    onSubmit: onSelectClient,
  });

  const value = {
    form,
    endTask,
    activeCall,
    isCompletely,
    selectClientForm,
    isCallTransferred,
    setCallTransferred,
    setHangUpDialogVisible,
    setSubmitDialogVisible,
  };
  const callFlowDeadline = localStorage.getItem('callFlowDeadline') === 'true';

  const submitCaseAndClose = () => {
    const { isValid, submitForm } = form;
    if (isValid) {
      isCompletely.current = true;
      submitForm();
    } else {
      submitForm();
    }
  };

  const submitCaseForm = (completely: boolean) => {
    isCompletely.current = completely;
    form.submitForm();
  };

  const onHangUp = (submit: boolean) => {
    if (
      currentTask?.type !== ActionQueueType.Voice &&
      currentTask?.type !== ActionQueueType.Video &&
      currentTask?.type !== ActionQueueType.Chat
    ) {
      submitCaseForm(true);
    } else {
      endTask({ end: true, caseId: initialFormikValues?.id });
      if (submit) {
        submitCaseAndClose();
      }
    }
  };

  const onSubmitForm = async (completely: boolean) => {
    if (completely && currentTask?.type === ActionQueueType.Voice) {
      endTask({ end: true });
    }
    submitCaseForm(completely);
  };

  return (
    <CallContext.Provider value={value}>
      {children}

      {isHangUpDialogVisible && currentTask && (
        <HangUpDialog
          task={currentTask}
          onHangUp={onHangUp}
          open={isHangUpDialogVisible}
          setOpen={setHangUpDialogVisible}
        />
      )}

      <SubmitCaseDialog
        onSubmit={onSubmitForm}
        open={isSubmitDialogVisible}
        setOpen={setSubmitDialogVisible}
        isCall={currentTask?.type === ActionQueueType.Voice}
        isVideo={currentTask?.type === ActionQueueType.Video}
      />

      <ConnectionLostDialog
        task={currentTask}
        onSubmit={submitCaseAndClose}
        setOpen={setLostDialogVisible}
        onCallback={() => setCallbackOpened(true)}
        open={isLostDialogVisible && !callFlowDeadline}
        onReconnect={
          isReconnectAvailable && isLostDialogVisible ? onReconnect : undefined
        }
      />

      <ReconnectDialog
        onReconnect={onReconnect}
        onSubmit={submitCaseAndClose}
        open={isReconnectDialogVisible}
        setOpen={setReconnectialogVisible}
      />
    </CallContext.Provider>
  );
};
